import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { createApiUrl } from '../utils/apiConfig';
import { getCurrencyInfo, formatPrice } from '../utils/currencyService';
import './Basket.css';

const Basket = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [basketItems, setBasketItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [_currencySymbol, setCurrencySymbol] = useState('£');

  useEffect(() => {
    // Initialize currency
    const initializeCurrency = async () => {
      try {
        const currencyInfo = await getCurrencyInfo();
        setCurrencySymbol(currencyInfo.symbol);
      } catch (error) {
        console.error('Error fetching currency:', error);
        setCurrencySymbol('£'); // Default fallback
      }
    };

    initializeCurrency();

    const fetchBasketItems = async () => {
      try {
        const response = await axios.get(createApiUrl('/api/basket'), {
          withCredentials: true
        });
        setBasketItems(response.data.basketItems || []);
        setTotalPrice(response.data.totalPrice || 0);
        setDiscountCode(response.data.discountCode || '');
      } catch (error) {
        console.error('Error fetching basket items:', error);
      }
    };

    fetchBasketItems();

    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  const _formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDuration = (duration) => {
    if (duration < 30) {
      return `${duration} days`;
    } else if (duration % 365 === 0) {
      const years = duration / 365;
      return `${years} year${years > 1 ? 's' : ''}`;
    } else {
      return `${Math.ceil(duration / 30)} months`;
    }
  };

  const handleQuantityChange = async (itemId, change) => {
    try {
      const isOrgAdmin = user?.isOrgAdmin || user?.userType === 'organization';
      const isSchAdmin = user?.isSchAdmin;
      
      let endpoint, requestBody;
      
      if (isOrgAdmin) {
        endpoint = '/api/org-update-basket';
        requestBody = { itemId, change };
      } else if (isSchAdmin) {
        endpoint = '/api/sch-update-basket';
        requestBody = { itemId, change };
      } else {
        endpoint = '/api/update-basket';
        requestBody = { itemId: { planId: itemId }, change, totalPrice: totalPrice };
      }

      const response = await axios.post(endpoint, requestBody, {
        withCredentials: true
      });

      if (response.data.success) {
        setBasketItems(response.data.basket);
        setTotalPrice(response.data.totalPrice);
      } else {
        alert(response.data.error);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const handleRemovePlan = async (_planId) => {
    try {


      
      const response = await axios.post(createApiUrl('/api/clear-basket'), {}, {
        withCredentials: true
      });
      
      // The backend redirects, so we just clear the basket locally
      setBasketItems([]);
      setTotalPrice(0);
      setDiscountCode('');
      
      // Use the redirect URL from the backend
      if (response.data.redirectUrl) {
        navigate(response.data.redirectUrl);
      } else {
        navigate('/teacher-dashboard');
      }
      
    } catch (error) {
      console.error('Error removing plan:', error);
      console.error('Error response:', error.response?.data);
      // If there's an error, still clear the basket locally
      setBasketItems([]);
      setTotalPrice(0);
      setDiscountCode('');
      navigate('/teacher-dashboard');
    }
  };

  const handleCheckout = () => {
    navigate('/payment');
  };

  return (
    <div className="basket-fullwidth">
      <h1 className="my-4 basket-title">Your Basket</h1>
      {!isOnline && (
        <div className="connection-status">You are currently offline</div>
      )}

      <section>
        {basketItems && basketItems.length > 0 ? (
          <>
            <div className="basket-items">
              {basketItems.map((item, index) => {
                const isSchoolAdmin = user?.isSchAdmin;
                return (
                <div key={index} className="product">
                  <h4>{item.planId && item.planId.name ? item.planId.name : (item.name || 'Plan')}</h4>
                  <p>Duration: {formatDuration(item.duration)}</p>
                  <p className="price">Price: {formatPrice(item.price)}</p>
                  <p className="preserve-whitespace">Description: {item.description}</p>

                  {item.type === 'workshop' ? (
                    <p>Workshop Date: {new Date(item.workshopDate).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}</p>
                  ) : (
                    <>
                      <h4>Assets:</h4>
                      <div className="basket-assets-row">
                        {item.assets && item.assets.length > 0 ? (
                          item.assets.map((asset, assetIndex) => (
                            <div key={assetIndex} className="basket-asset-col">
                              <div className="border rounded p-3 mb-3">
                                <p>Name: {asset.name}</p>
                                <p className="preserve-whitespace">Description: {asset.description}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>No assets available.</p>
                        )}
                      </div>
                    </>
                  )}

                  <div className="quantity-controls">
                    <button
                      type="button"
                      className="quantity-button"
                      onClick={() => {
                        const planId = typeof item.planId === 'object' ? item.planId._id || item.planId : item.planId;
                        handleQuantityChange(planId, -1);
                      }}
                      disabled={item.type === 'workshop' || isSchoolAdmin}
                      title={isSchoolAdmin ? 'School admins can only have one plan' : ''}
                      style={isSchoolAdmin ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      -
                    </button>
                    <span className="quantity-box">{item.quantity || 1}</span>
                    <button
                      type="button"
                      className="quantity-button"
                      onClick={() => {
                        const planId = typeof item.planId === 'object' ? item.planId._id || item.planId : item.planId;
                        handleQuantityChange(planId, 1);
                      }}
                      disabled={item.type === 'workshop' || isSchoolAdmin}
                      title={isSchoolAdmin ? 'School admins can only have one plan' : ''}
                      style={isSchoolAdmin ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      +
                    </button>
                  </div>

                  <div className="price-section">
                    {/* Debug: checking for any variables that might render as 0 */}
                                         {item.discount && item.discount > 0 && (
                      <div className="discount-info">
                        <p className="original-price">Original Price: {formatPrice(item.price * item.quantity)}</p>
                        <p className="discount-percentage">Discount: {(item.discount * 100).toFixed(0)}% off</p>
                        <p className="savings">You Save: {formatPrice((item.price * item.quantity) - item.totalPrice)}</p>
                      </div>
                    )}
                    <p className="price">
                      Total Price: {formatPrice(item.totalPrice || (item.price * item.quantity))}
                    </p>
                  </div>

                  <div className="form-group">
                    <div className="discount-code-container">
                      <label htmlFor="discountCode" style={{ color: '#FF8C00' }}>Discount Code:</label>
                      <input
                        type="text"
                        id="discountCode"
                        className="form-control discount-code-input"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="Enter discount code"
                      />
                    </div>
                    <span className="discount-message">
                      Discount will be applied on the Stripe Payment page
                    </span>
                  </div>

                  <div className="button-container">
                    <button
                      type="button"
                      className="btn btn-primary left-btn"
                      onClick={handleCheckout}
                    >
                      Proceed to Checkout
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger right-btn"
                      onClick={() => handleRemovePlan(item.planId ? item.planId._id : item._id)}
                    >
                      Clear the Basket
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        ) : (
          <p>Your basket is empty.</p>
        )}
      </section>
    </div>
  );
};

export default Basket; 